/* ─────────────────────────────────────────────────────────────
   CoberturasModule.jsx — DMU 614
   Pantalla "Coberturas del día" para supervisor_general_pipas/admin.
   Muestra las 19 pipas con estado del titular, estado de la pipa,
   cobertura activa y permite reasignar.
   La RUTA NO se toca — vive en pipa+zona.
   ───────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import ModoDemoBanner from "../components/ModoDemoBanner";
import "../styles/supervisor.css";
import "../styles/modal-parque.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADO_CHOFER_META = {
  ACTIVO:      { label: "Activo",       icon: "fa-circle-check",        bg: "#DCFCE7", color: "#166534" },
  VACACIONES:  { label: "Vacaciones",   icon: "fa-umbrella-beach",      bg: "#FEF3C7", color: "#92400E" },
  INCAPACIDAD: { label: "Incapacidad",  icon: "fa-kit-medical",         bg: "#FEE2E2", color: "#991B1B" },
  PERMISO:     { label: "Permiso",      icon: "fa-clipboard-list",      bg: "#E0E7FF", color: "#3730A3" },
  SIN_TITULAR: { label: "Sin titular",  icon: "fa-user-slash",          bg: "#F1F5F9", color: "#475569" },
};

const ESTADO_PIPA_META = {
  OPERATIVA:            { label: "Operativa",       icon: "fa-circle-check",        bg: "#DCFCE7", color: "#166534" },
  EN_TALLER:            { label: "En taller",       icon: "fa-screwdriver-wrench",  bg: "#FEF3C7", color: "#92400E" },
  PRESTADA_A_OTRA_ZONA: { label: "Prestada",        icon: "fa-arrow-right-arrow-left", bg: "#E0F2FE", color: "#075985" },
  BAJA:                 { label: "Baja",            icon: "fa-ban",                 bg: "#FEE2E2", color: "#991B1B" },
};

const MOTIVOS = ["VACACIONES", "INCAPACIDAD", "PERMISO", "PRESTAMO_PIPA", "PIPA_EN_TALLER", "OTRO"];

export default function CoberturasModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [verDemo, setVerDemo] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("TODAS"); // TODAS | NECESITAN | ACTIVAS_COB
  const [asignar, setAsignar] = useState(null);     // row para asignar/modificar cobertura
  const [editarEstado, setEditarEstado] = useState(null); // titular para cambiar estado
  const [expandedId, setExpandedId] = useState(null);      // pipa.id expandida (accordion)

  const puede = ["supervisor_general_pipas", "admin"].includes(user?.rol);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/coberturas/dashboard`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar coberturas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (puede) cargar(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    if (filtro === "NECESITAN") return data.rows.filter(r => r.necesita_cobertura);
    if (filtro === "ACTIVAS_COB") return data.rows.filter(r => r.cobertura?.estado === "ACTIVA");
    return data.rows;
  }, [data, filtro]);

  return (
    <div className="sup-shell" data-testid="page-coberturas">
      <ModoDemoBanner verDemo={verDemo} setVerDemo={setVerDemo} />
      <header className="sup-topbar">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/hub")} className="iconbtn-light" data-testid="btn-back-hub">
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div>
            <div className="sup-title"><i className="fa-solid fa-people-arrows mr-2"/>Coberturas del día</div>
            <div className="sup-sub">
              {user?.nombre} · <span className="text-sky-300">{user?.rol}</span> · {new Date().toLocaleDateString("es-MX",{weekday:"long",day:"2-digit",month:"long"})}
            </div>
          </div>
        </div>
        <button onClick={logout} className="iconbtn-light" data-testid="btn-logout">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </header>

      <main className="sup-main">
        {!puede ? (
          <div className="text-center py-12 text-slate-500" data-testid="no-permisos">
            <i className="fa-solid fa-lock text-3xl mb-3"/>
            <div>Solo supervisor general y admin pueden gestionar coberturas.</div>
          </div>
        ) : (
          <>
            {/* Resumen + filtros STICKY (se quedan visibles al scrollear) */}
            <div className="cob-sticky" data-testid="cob-sticky">
              {data?.resumen && (
                <section className="resumen-pipas" data-testid="resumen-coberturas">
                  <span><i className="fa-solid fa-users mr-1"/><strong>{data.resumen.choferes_activos}</strong> activos</span>
                  <span className="resumen-chip" data-testid="r-ausentes"><i className="fa-solid fa-umbrella-beach text-amber-400"/> {data.resumen.choferes_ausentes} ausentes</span>
                  <span className="resumen-chip" data-testid="r-taller"><i className="fa-solid fa-screwdriver-wrench text-amber-400"/> {data.resumen.pipas_en_taller} en taller</span>
                  <span className="resumen-chip" data-testid="r-prestadas"><i className="fa-solid fa-arrow-right-arrow-left text-sky-400"/> {data.resumen.pipas_prestadas} prestadas</span>
                  <span className="ml-auto text-xs opacity-70">{data.resumen.total_pipas} pipas totales</span>
                </section>
              )}

              {/* Filtros */}
              <div className="flex flex-wrap gap-2 mt-2" data-testid="filtros-cob">
                {[
                  ["TODAS",       `Todas (${data?.rows?.length || 0})`,                                    "fa-list"],
                  ["NECESITAN",   `Necesitan cobertura (${data?.rows?.filter(r=>r.necesita_cobertura).length || 0})`, "fa-bell"],
                  ["ACTIVAS_COB", `Con cobertura activa (${data?.rows?.filter(r=>r.cobertura?.estado==="ACTIVA").length || 0})`, "fa-check"],
                ].map(([k, lbl, ic]) => (
                  <button
                    key={k}
                    className="bandeja-conteo-chip"
                    data-testid={`filtro-${k}`}
                    data-active={filtro === k}
                    onClick={() => setFiltro(k)}
                    style={{
                      background: filtro === k ? "#0F172A" : "#F8FAFC",
                      color: filtro === k ? "white" : "#0F172A",
                      borderColor: "#CBD5E1",
                    }}
                  ><i className={`fa-solid ${ic} mr-1.5`}/>{lbl}</button>
                ))}
                <button className="btn-ghost-sm ml-auto" onClick={cargar} data-testid="btn-refrescar">
                  <i className="fa-solid fa-rotate mr-1"/>Refrescar
                </button>
              </div>
            </div>

            {loading && <div className="text-center py-6 text-sm text-slate-500" data-testid="loading">Cargando…</div>}

            <div className="cob-list mt-3" data-testid="lista-coberturas">
              {rows.map(r => (
                <FilaPipa
                  key={r.pipa.id}
                  row={r}
                  abierto={expandedId === r.pipa.id}
                  onToggle={() => setExpandedId(prev => prev === r.pipa.id ? null : r.pipa.id)}
                  onAsignar={() => setAsignar(r)}
                  onEditarEstado={() => r.titular && setEditarEstado(r.titular)}
                  onCerrarCobertura={async () => {
                    if (!r.cobertura) return;
                    if (!window.confirm(`¿Cerrar cobertura activa de pipa ${r.pipa.id_pipa}?`)) return;
                    try {
                      await axios.put(`${API}/coberturas/${r.cobertura.id}/cerrar`);
                      toast.success(`Cobertura de pipa ${r.pipa.id_pipa} cerrada`);
                      cargar();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error");
                    }
                  }}
                  onQuitarTaller={async () => {
                    if (!window.confirm(`¿Confirmas que la pipa ${r.pipa.id_pipa} regresó de taller y vuelve a estar OPERATIVA?`)) return;
                    try {
                      await axios.put(`${API}/coberturas/pipa/${r.pipa.id}/quitar-taller`);
                      toast.success(`Pipa ${r.pipa.id_pipa} fuera de taller`);
                      cargar();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error al quitar de taller");
                    }
                  }}
                  onCerrarPrestamo={async () => {
                    const destino = r.pipa.zona_prestamo_destino || "otra zona";
                    if (!window.confirm(`¿Confirmas que la pipa ${r.pipa.id_pipa} ya regresó del préstamo a ${destino} y vuelve a operar en ${r.pipa.zona_asignada || "su zona"}?`)) return;
                    try {
                      await axios.put(`${API}/coberturas/pipa/${r.pipa.id}/cerrar-prestamo`);
                      toast.success(`Pipa ${r.pipa.id_pipa} ya no está prestada`);
                      cargar();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Error al cerrar préstamo");
                    }
                  }}
                />
              ))}
            </div>

            {asignar && (
              <DialogoAsignar
                row={asignar}
                onClose={() => setAsignar(null)}
                onOk={() => { setAsignar(null); cargar(); }}
              />
            )}
            {editarEstado && (
              <DialogoEstadoChofer
                chofer={editarEstado}
                onClose={() => setEditarEstado(null)}
                onOk={() => { setEditarEstado(null); cargar(); }}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

/* ─── Fila de pipa — vista compacta tipo lista + accordion ───────
   Línea 1: número grande · zona · tipo agua · badge estado · chevron
   Línea 2: titular · resumen del día
   Al tocar la fila se expande con detalles y botones de acción rápida.
   ─────────────────────────────────────────────────────────────── */
function FilaPipa({ row, abierto, onToggle, onAsignar, onEditarEstado, onCerrarCobertura, onQuitarTaller, onCerrarPrestamo }) {
  const { pipa, titular, cobertura } = row;
  const ec = titular ? (ESTADO_CHOFER_META[titular.estado_chofer] || ESTADO_CHOFER_META.ACTIVO) : ESTADO_CHOFER_META.SIN_TITULAR;
  const ep = ESTADO_PIPA_META[pipa.estado_operativo] || ESTADO_PIPA_META.OPERATIVA;
  // Detección DEFENSIVA del estado: tolera variantes en BD de prod donde el
  // campo `estado_operativo` quizás no esté sincronizado con `estado`,
  // o tenga otro casing/espacios. Si CUALQUIERA de los dos campos dice
  // taller/prestada, mostramos el botón correspondiente.
  const _estRaw = String(pipa.estado_operativo ?? pipa.estado ?? "").toUpperCase().trim();
  const _estAlt = String(pipa.estado ?? "").toUpperCase().trim();
  const _est    = _estRaw || _estAlt;
  const enTaller = ["EN_TALLER", "TALLER", "EN TALLER", "EN-TALLER"].includes(_est)
                || ["EN_TALLER", "TALLER", "EN TALLER", "EN-TALLER"].includes(_estAlt);
  const prestada = _est.startsWith("PRESTAD") || _estAlt.startsWith("PRESTAD");
  const tieneCobActiva = cobertura?.estado === "ACTIVA";

  // Resumen de la línea 2 según situación del día
  let situacionTxt = "sin cobertura";
  if (cobertura?.estado === "ACTIVA") {
    situacionTxt = `cubre: ${cobertura.chofer_cobertura_nombre || "—"}`;
  } else if (cobertura?.estado === "PENDIENTE_ASIGNAR_COBERTURA") {
    situacionTxt = "pendiente de cobertura";
  } else if (row.necesita_cobertura) {
    situacionTxt = "necesita cobertura";
  } else if (prestada) {
    situacionTxt = `prestada a ${pipa.zona_prestamo_destino || "otra zona"}`;
  }

  return (
    <article
      className="cob-row"
      data-testid={`row-pipa-${pipa.id_pipa}`}
      data-necesita={row.necesita_cobertura}
      data-dedicada={pipa.es_dedicada}
      data-open={abierto || undefined}
    >
      <button
        type="button"
        className="cob-row-head"
        onClick={onToggle}
        aria-expanded={abierto}
        data-testid={`row-head-${pipa.id_pipa}`}
      >
        <span className="cob-num">#{pipa.id_pipa}</span>
        <span className="min-w-0">
          <span className="cob-meta-l1">
            <span className="pill">{pipa.zona_asignada || "—"}</span>
            <span>{pipa.tipo_agua || "—"}</span>
            {pipa.es_dedicada && <span className="badge-dedicada" style={{padding:"0.1rem 0.4rem"}}>DEDICADA</span>}
          </span>
          <span className="cob-meta-l2">
            <strong>{titular?.nombre || "Sin titular"}</strong>
            <span style={{opacity: 0.7}}>· {situacionTxt}</span>
          </span>
        </span>
        <span className="cob-badge" style={{background: ep.bg, color: ep.color}} data-testid={`estado-pipa-${pipa.id_pipa}`}>
          <i className={`fa-solid ${ep.icon}`}/>{ep.label}
        </span>
        <i className="fa-solid fa-chevron-down cob-chev" aria-hidden="true"/>
      </button>

      {abierto && (
        <div className="cob-acc-body" data-testid={`acc-body-${pipa.id_pipa}`}>
          <div className="cob-acc-block">
            <div className="lbl">Pipa</div>
            <div>
              {pipa.categoria || "—"}
              <span className="text-slate-400"> · turno {pipa.turno || "—"}</span>
              {prestada && pipa.zona_prestamo_destino && (
                <div className="text-xs text-sky-700 mt-0.5">
                  <i className="fa-solid fa-arrow-right mr-1"/>destino préstamo: <strong>{pipa.zona_prestamo_destino}</strong>
                </div>
              )}
            </div>
          </div>

          <div className="cob-acc-block" data-testid={`titular-${pipa.id_pipa}`}>
            <div className="lbl">Titular</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">{titular?.nombre || "Sin asignar"}</span>
              {titular?.n_empleado && <span className="font-mono text-xs text-slate-500">({titular.n_empleado})</span>}
              <span className="cob-badge" style={{background: ec.bg, color: ec.color}} data-testid={`estado-chofer-${pipa.id_pipa}`}>
                <i className={`fa-solid ${ec.icon}`}/>{ec.label}
              </span>
            </div>
            {titular?.estado_desde && (
              <div className="text-xs text-slate-500 mt-1">
                {titular.estado_desde}{titular.estado_hasta ? ` → ${titular.estado_hasta}` : ""}
                {titular.estado_motivo ? ` · ${titular.estado_motivo}` : ""}
              </div>
            )}
          </div>

          {cobertura && (
            <div className="cob-acc-block sm:col-span-2" data-testid={`cobertura-${pipa.id_pipa}`} data-estado={cobertura.estado}>
              <div className="lbl">Cobertura</div>
              <div>
                <i className={`fa-solid ${cobertura.estado === "ACTIVA" ? "fa-circle-check text-emerald-600" : "fa-clock text-amber-600"} mr-1`}/>
                <strong>{cobertura.estado === "ACTIVA" ? "Cubre hoy:" : "Pendiente:"}</strong>{" "}
                {cobertura.chofer_cobertura_nombre || "—"}
                <span className="text-xs text-slate-500"> · {cobertura.motivo} · {cobertura.fecha_inicio || "—"}{cobertura.fecha_fin ? ` → ${cobertura.fecha_fin}` : ""}</span>
              </div>
            </div>
          )}

          {/* Botones de acción rápida — visibilidad contextual */}
          <div className="cob-acc-actions">
            {enTaller && (
              <button className="cob-btn cob-btn-taller" onClick={onQuitarTaller} data-testid={`btn-quitar-taller-${pipa.id_pipa}`}>
                <i className="fa-solid fa-screwdriver-wrench"/>Quitar de taller
              </button>
            )}
            {prestada && (
              <button className="cob-btn cob-btn-prestamo" onClick={onCerrarPrestamo} data-testid={`btn-cerrar-prestamo-${pipa.id_pipa}`}>
                <i className="fa-solid fa-arrow-right-arrow-left"/>Cerrar préstamo
              </button>
            )}
            {tieneCobActiva && (
              <button className="cob-btn cob-btn-cerrar" onClick={onCerrarCobertura} data-testid={`btn-cerrar-${pipa.id_pipa}`}>
                <i className="fa-solid fa-xmark"/>Cerrar cobertura del día
              </button>
            )}
            {!cobertura && row.necesita_cobertura && (
              <button className="cob-btn cob-btn-primary" onClick={onAsignar} data-testid={`btn-asignar-${pipa.id_pipa}`}>
                <i className="fa-solid fa-user-plus"/>Asignar cobertura
              </button>
            )}
            {cobertura && (
              <button className="cob-btn cob-btn-ghost" onClick={onAsignar} data-testid={`btn-modificar-${pipa.id_pipa}`}>
                <i className="fa-solid fa-rotate"/>Modificar cobertura
              </button>
            )}
            {titular && (
              <button className="cob-btn cob-btn-ghost" onClick={onEditarEstado} data-testid={`btn-editar-estado-${pipa.id_pipa}`}>
                <i className="fa-solid fa-pen-to-square"/>Modificar titular
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

/* ─── Diálogo: cambiar estado del chofer ──────────────────── */
function DialogoEstadoChofer({ chofer, onClose, onOk }) {
  const [estado, setEstado] = useState(chofer.estado_chofer || "ACTIVO");
  const [desde, setDesde]   = useState(chofer.estado_desde || new Date().toISOString().slice(0,10));
  const [hasta, setHasta]   = useState(chofer.estado_hasta || "");
  const [motivo, setMotivo] = useState(chofer.estado_motivo || "");
  const [busy, setBusy] = useState(false);

  const enviar = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/choferes/${chofer.id}/estado`, {
        estado, desde: desde || null, hasta: hasta || null, motivo: motivo || null,
      });
      toast.success(`Estado de ${chofer.nombre} → ${estado}`);
      onOk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="mp-bg" onClick={onClose} data-testid="dlg-estado">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{maxWidth: 460}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title"><i className="fa-solid fa-user-pen mr-2"/>Estado del chofer</h2>
            <div className="mp-id">{chofer.nombre} <span className="font-mono">({chofer.n_empleado})</span></div>
          </div>
          <button onClick={onClose} className="mp-x" data-testid="btn-cerrar-dlg-estado"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"1rem"}}>
          <div>
            <label className="mp-validar-lbl">Estado *</label>
            <select className="mp-select" value={estado} onChange={(e) => setEstado(e.target.value)} data-testid="select-estado-chofer">
              {Object.entries(ESTADO_CHOFER_META).filter(([k]) => k !== "SIN_TITULAR").map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mp-validar-lbl">Desde</label>
              <input type="date" className="mp-select" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="input-desde"/>
            </div>
            <div>
              <label className="mp-validar-lbl">Hasta</label>
              <input type="date" className="mp-select" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="input-hasta"/>
            </div>
          </div>
          <div>
            <label className="mp-validar-lbl">Motivo / notas</label>
            <textarea rows={3} maxLength={200} value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="textarea-motivo-estado" placeholder="Ej: vacaciones programadas, incapacidad médica…"/>
          </div>
        </div>
        <footer className="mp-foot">
          <button className="btn-primary" disabled={busy} onClick={enviar} data-testid="btn-confirm-estado">
            {busy ? "Guardando…" : "Guardar cambio"}
          </button>
          <button className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}}>Cancelar</button>
        </footer>
      </div>
    </div>
  );
}

/* ─── Diálogo: asignar/modificar cobertura ────────────────── */
function DialogoAsignar({ row, onClose, onOk }) {
  const { pipa, titular, cobertura } = row;
  const [choferes, setChoferes] = useState([]);
  const [choferId, setChoferId] = useState(cobertura?.chofer_cobertura_id || "");
  const [motivo, setMotivo] = useState(
    cobertura?.motivo ||
    (pipa.estado_operativo === "EN_TALLER" ? "PIPA_EN_TALLER" :
     pipa.estado_operativo === "PRESTADA_A_OTRA_ZONA" ? "PRESTAMO_PIPA" :
     titular?.estado_chofer && titular.estado_chofer !== "ACTIVO" ? titular.estado_chofer : "VACACIONES")
  );
  const hoy = new Date().toISOString().slice(0,10);
  const [fechaInicio, setFechaInicio] = useState(cobertura?.fecha_inicio || hoy);
  const [fechaFin, setFechaFin] = useState(cobertura?.fecha_fin || "");
  const [notas, setNotas] = useState(cobertura?.notas || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/coberturas/choferes-disponibles`, { params: { pipa_id: pipa.id } })
      .then(r => setChoferes(r.data?.choferes || []))
      .catch(() => setChoferes([]));
  }, [pipa.id]);

  const enviar = async () => {
    if (!choferId) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coberturas/asignar`, {
        pipa_id: pipa.id, chofer_cobertura_id: choferId,
        motivo, fecha_inicio: fechaInicio, fecha_fin: fechaFin || null,
        notas: notas || null,
      });
      toast.success(`Pipa ${pipa.id_pipa} ${r.data.modificacion ? "modificada" : "asignada"}`);
      onOk();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al asignar");
    } finally { setBusy(false); }
  };

  const esModif = !!cobertura;

  return (
    <div className="mp-bg" onClick={onClose} data-testid="dlg-asignar-cob">
      <div className="mp-card" onClick={(e) => e.stopPropagation()} style={{maxWidth: 520}}>
        <header className="mp-head">
          <div className="mp-head-text">
            <h2 className="mp-title">
              <i className="fa-solid fa-user-plus mr-2"/>{esModif ? "Modificar" : "Asignar"} cobertura
            </h2>
            <div className="mp-id">
              <strong>Pipa #{pipa.id_pipa}</strong> · {pipa.categoria} · zona {pipa.zona_asignada || "—"}
            </div>
          </div>
          <button onClick={onClose} className="mp-x" data-testid="btn-cerrar-dlg-asignar"><i className="fa-solid fa-xmark"/></button>
        </header>
        <div className="mp-body" style={{display:"flex", flexDirection:"column", gap:"1rem"}}>

          {/* Aviso DEDICADA */}
          {pipa.es_dedicada && (
            <div className="mp-rep-hint" style={{background: "#FEF3C7", borderColor: "#F59E0B", color: "#92400E"}} data-testid="aviso-dedicada">
              <i className="fa-solid fa-triangle-exclamation mr-1"/>
              <strong>Pipa dedicada ({pipa.categoria}).</strong> Asegúrate de que el chofer conoce el procedimiento especial (camellones, tinacos o quinta rueda).
            </div>
          )}

          {/* Contexto del titular */}
          {titular && (
            <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded" data-testid="contexto-titular">
              <strong>Titular:</strong> {titular.nombre} ({titular.n_empleado || "—"}) · estado: <strong>{titular.estado_chofer}</strong>
              {titular.estado_motivo && <> · {titular.estado_motivo}</>}
            </div>
          )}

          <div>
            <label className="mp-validar-lbl">Chofer que cubre *</label>
            <select className="mp-select cob-select-chofer" value={choferId} onChange={(e) => setChoferId(e.target.value)} data-testid="select-chofer-cobertura">
              <option value="">— Selecciona un chofer ACTIVO disponible —</option>
              {choferes.map(c => {
                const pipas = (c.pipas_asignadas || []).filter(Boolean);
                const pipaTxt = pipas.length ? ` · pipa ${pipas.join(",")}` : "";
                return (
                  <option key={c.id} value={c.id} title={`${c.nombre} (${c.n_empleado})${pipaTxt}`}>
                    {c.nombre} ({c.n_empleado}){pipaTxt}
                  </option>
                );
              })}
            </select>
            <div className="text-xs text-slate-500 mt-1">{choferes.length} disponibles · solo choferes ACTIVOS sin otra cobertura activa</div>
          </div>

          <div>
            <label className="mp-validar-lbl">Motivo *</label>
            <select className="mp-select" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="select-motivo-cob">
              {MOTIVOS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mp-validar-lbl">Desde *</label>
              <input type="date" className="mp-select" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} data-testid="input-fecha-inicio"/>
            </div>
            <div>
              <label className="mp-validar-lbl">Hasta (opcional)</label>
              <input type="date" className="mp-select" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} data-testid="input-fecha-fin"/>
            </div>
          </div>

          <div>
            <label className="mp-validar-lbl">Notas (opcional)</label>
            <textarea rows={3} maxLength={300} value={notas} onChange={(e) => setNotas(e.target.value)} data-testid="textarea-notas-cob"/>
          </div>

          <div className="text-xs text-slate-500 bg-emerald-50 p-2 rounded border border-emerald-200">
            <i className="fa-solid fa-route mr-1 text-emerald-600"/>
            <strong>La ruta no cambia.</strong> El chofer que cubre hereda automáticamente la ruta de esta pipa en zona {pipa.zona_asignada || "—"}.
          </div>
        </div>
        <footer className="mp-foot">
          <button className="btn-primary" disabled={!choferId || busy} onClick={enviar} data-testid="btn-confirm-cob">
            {busy ? "Guardando…" : (esModif ? "Confirmar cambio" : "Asignar cobertura")}
          </button>
          <button className="btn-ghost" onClick={onClose} style={{padding:"0.55rem 1rem"}}>Cancelar</button>
        </footer>
      </div>
    </div>
  );
}
